import { action, get } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { Changeset } from 'ember-changeset';
import metricValidations from 'scaleflow/validators/metric';
import targetValidations from 'scaleflow/validators/target';

import type StoreService from 'scaleflow/services/store';

interface GoalsMetricWizardSignature {
  Args: {
    goal: { metrics: unknown[]; targets: unknown };
    closeWizard: () => void;
  };
  Blocks: {
    default: [];
  };
  Element: null;
}

type AnyRecord = any;

export default class GoalsMetricWizardComponent extends Component<GoalsMetricWizardSignature> {
  @tracked activeStep = 0;
  @tracked metricChangeset: AnyRecord = {};
  @tracked targetChangeset: AnyRecord = {};
  @tracked metricsSearch = '';
  @tracked isNewMetric = false;
  @tracked targetChangesets: AnyRecord[] = [];
  @tracked selectedMetrics: AnyRecord[] = [];
  @tracked metric: AnyRecord = null;
  @tracked target: AnyRecord = null;

  MetricValidations = metricValidations;
  TargetValidations = targetValidations;

  @service declare betaToggle: AnyRecord;
  @service declare store: StoreService;
  @service declare router: AnyRecord;

  @action
  setActiveStep(index: number) {
    this.activeStep = index;
  }

  get isDisabled() {
    if (this.isNewMetric) {
      return this.metricChangeset.isInvalid || this.metricChangeset.isPristine;
    } else {
      return !this.selectedMetrics.length;
    }
  }

  metricValidator({ key, newValue }: { key: string; newValue: AnyRecord }) {
    const validator = get(metricValidations, key);
    if (typeof validator === 'function') {
      return validator(key, newValue);
    }
  }

  targetValidator({ key, newValue }: { key: string; newValue: AnyRecord }) {
    const validator = get(targetValidations, key);
    if (typeof validator === 'function') {
      return validator(key, newValue);
    }
  }

  @action
  onDescriptionChange(changeset: AnyRecord, value: unknown) {
    changeset.set('description', value);
  }

  @action
  async saveMetrics() {
    if (this.isNewMetric) {
      this.metricChangeset.changes.forEach(
        (change: { key: string; value: unknown }) => {
          this.metric.set(change.key, change.value);
        },
      );
      await this.metric.save();
      const target = this.store.createRecord('target', {
        target_value: this.targetChangeset.target_value,
        end_date: this.targetChangeset.end_date,
        goal: this.args.goal,
        metric: this.metric,
      });
      await target.save();
    } else {
      for (let i = 0; i < this.selectedMetrics.length; i++) {
        const target = this.store.createRecord('target', {
          target_value: this.targetChangesets[i].target_value,
          end_date: this.targetChangesets[i].end_date,
          goal: this.args.goal,
          metric: this.selectedMetrics[i],
        });
        await target.save();
      }
    }
    this.args.closeWizard();
  }

  @action
  rollbackChanges(changeset?: AnyRecord) {
    if (!changeset) {
      this.metricChangeset?.rollback?.();
      this.targetChangeset?.rollback?.();
      this.clearSelectedMetrics();
    } else {
      changeset.rollback();
    }
  }

  get metrics() {
    const goalMetrics = this.args.goal.metrics.reduce(
      (acc: Record<string, boolean>, item: AnyRecord) => {
        acc[item.content.id] = true;
        return acc;
      },
      {},
    );
    return this.store
      .peekAll('metric')
      .filter((metric: AnyRecord) => !goalMetrics[metric.id]);
  }

  @action
  targetChangesetFactory() {
    const target = this.store.createRecord('target', {}) as object;
    return Changeset(target, this.targetValidator.bind(this));
  }

  @action
  clearSelectedMetrics() {
    for (let i = 0; i < this.selectedMetrics.length; i++) {
      this.selectedMetrics[i].deleteRecord();
      this.targetChangesets[i].rollback();
    }
    this.selectedMetrics = [];
    this.targetChangesets = [];
  }

  @action
  handleMetricSelect(metric: AnyRecord, e: Event) {
    const checked = (e.target as HTMLInputElement).checked;
    if (checked === undefined) return;
    const metrics = this.selectedMetrics;
    const targetChangesets = this.targetChangesets;
    if (checked) {
      metrics.push(metric);
      targetChangesets.push(this.targetChangesetFactory());
    } else {
      const index = metrics.findIndex(
        (_metric: AnyRecord) => _metric.id === metric.id,
      );
      if (index < 0) return;
      metrics.splice(index, 1);
      targetChangesets.splice(index, 1);
    }
    this.selectedMetrics = metrics;
    this.targetChangesets = targetChangesets;
  }

  @action
  showMetricForm() {
    this.isNewMetric = true;
    this.metric = this.store.createRecord('metric', {});
    this.metricChangeset = Changeset(
      this.metric as object,
      this.metricValidator.bind(this),
    );
    this.target = this.store.createRecord('target', {});
    this.targetChangeset = Changeset(
      this.target as object,
      this.targetValidator.bind(this),
    );
  }

  @action
  hideMetricForm() {
    this.isNewMetric = false;
    this.metricChangeset = null;
    this.metric?.deleteRecord();
    this.metric = null;
    this.targetChangeset = null;
    this.target?.deleteRecord();
    this.target = null;
  }

  @action
  onFormCancel() {
    this.rollbackChanges();
    this.metric?.deleteRecord?.();
    this.metric = null;
    this.target?.deleteRecord?.();
    this.target = null;
    this.activeStep = 0;
    this.isNewMetric = false;
    this.args.closeWizard();
  }
}
